import { Divider } from "@redotech/arbiter/divider/divider";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { Currency } from "@redotech/money/currencies";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import type { RefundDestinationForm } from "@redotech/redo-customer-portal-shared/hooks/useRefundDestination/use-refund-destination-form";
import { useTranslation } from "@redotech/redo-i18n";
import { memo } from "react";
import { ConfirmationButtonGroup } from "./confirmation-button-group";
import * as css from "./edit-refund-method-modal.module.css";
import { RefundDestinationOptions } from "./refund-destination-options";
import { StickyFooter } from "./sticky-footer";

export interface EditRefundMethodModalProps {
  form: RefundDestinationForm;
  refundAmount: number;
  currency: Currency;
  saving: boolean;
  /** Shown under the recipient field. */
  validationMessage: string | undefined;
  /** Shown against the modal; a failed save is not a bad field. */
  saveError: string | undefined;
  onSave: () => void;
  onClose: () => void;
}

/**
 * Post-submission editing of where a refund is sent. Unlike the picker on the
 * review screen this saves on a button rather than on blur, so backing out
 * leaves the saved destination alone.
 */
export const EditRefundMethodModal = memo(function EditRefundMethodModal({
  form,
  refundAmount,
  currency,
  saving,
  validationMessage,
  saveError,
  onSave,
  onClose,
}: EditRefundMethodModalProps) {
  const { t } = useTranslation();
  const screenSize = useScreenType();

  return (
    <Flex
      className={
        screenSize === ScreenType.DESKTOP ? css.desktopCardLayout : undefined
      }
      data-ui="edit-refund-method"
      dir="column"
      gap="2xl"
      pt="3xl"
      px="3xl"
    >
      <Text as="h2" fontSize="lg" fontWeight="semibold" textColor="primary">
        {t("Edit refund method")}
      </Text>
      <Divider />
      <Flex dir="column" gap="3xl" pt="sm">
        <Text as="p" fontSize="md" textColor="secondary">
          {t("Choose where we should send your refund.")}
        </Text>
        <RefundDestinationOptions
          allowOriginalPayment={false}
          currency={currency}
          errorMessage={validationMessage}
          form={form}
          refundAmount={refundAmount}
          saving={saving}
        />
      </Flex>
      {!!saveError && (
        <Text
          as="p"
          data-ui="refund-method-save-error"
          fontSize="sm"
          textColor="error"
        >
          {saveError}
        </Text>
      )}
      <StickyFooter>
        <Flex dir="column" gap="3xl" pt="sm">
          <Divider />
          <ConfirmationButtonGroup
            primaryButtonProps={{
              dataTestId: "save-refund-method",
              disabled: saving || !form.isDirty || form.isIncomplete,
              onClick: onSave,
              pending: saving,
              text: t("Save"),
            }}
            secondaryButtonProps={{
              disabled: saving,
              onClick: onClose,
              text: t("Cancel"),
            }}
          />
        </Flex>
      </StickyFooter>
    </Flex>
  );
});
